import React, { useMemo, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Feather, Check, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import SoulHero from "@/components/soul/SoulHero";
import SoulBreadcrumb from "@/components/soul/SoulBreadcrumb";
import BreathPlayer from "@/components/soul/BreathPlayer";
import N8Emblem from "@/components/N8Emblem";
import {
  BREATH_INTENTIONS,
  findBreathPractice,
} from "@/pages/soul/breath/breathContent";

/**
 * BreathPractice — a single Breath doorway.
 *
 * This file renders EVERY individual Breath detail page (Conscious
 * Breathing, Extended Exhale, Box Breathing, 4-7-8, Alternate Nostril,
 * Coherence, etc.) by looking up the slug in `breathContent.js`. So any
 * structural refinement here applies to all practices automatically.
 *
 * EXPERIENCE RHYTHM (2026-02 refinement)
 *   SEE          → Hero (human-centered, warm sunlit treatment)
 *   UNDERSTAND   → The Practice (one-sentence bridge)
 *   PRACTICE     → Begin the Practice (primary interactive card)
 *   NOTICE       → What To Notice (invitations, not tasks)
 *   LEARN MORE   → Why / How / When (optional deeper understanding)
 *                  behind a quiet "Go deeper" divider
 *   SAFETY       → Gentle Note (understated, warm, never alarming)
 *
 * Modes within the page:
 *   1. "read"     → setup (duration + intention + Begin)
 *   2. "practice" → BreathPlayer runs
 *   3. "close"    → reflection prompt + gentle reminder + save to Journal
 *
 * Everything after the practice card is OPTIONAL deeper understanding.
 * The member never has to read it to begin.
 */
export default function BreathPractice() {
  const { slug } = useParams();
  const nav = useNavigate();
  const practice = useMemo(() => findBreathPractice(slug), [slug]);
  const [mode, setMode] = useState("read");
  const [minutes, setMinutes] = useState(() =>
    practice ? practice.recommendedMinutes[0] : 2,
  );
  const [intention, setIntention] = useState(null); // one of BREATH_INTENTIONS or null
  const [reflection, setReflection] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!practice) return <Navigate to="/soul/regulation-presence/breath" replace />;

  const handleComplete = () => setMode("close");
  const handleEnd = () => setMode("close");

  const saveToJournal = async () => {
    if (!reflection.trim() || saving) return;
    setSaving(true);
    try {
      const parts = [];
      parts.push(`Soul · Breath · ${practice.title}`);
      if (intention) parts.push(`Intention: ${intention}`);
      parts.push("");
      parts.push(`Reflection: ${practice.reflection}`);
      parts.push(reflection.trim());
      await endpoints.createJournal({
        prompt: `Soul · Breath · ${practice.title}`,
        content: parts.join("\n"),
        mood_tag: "reflect",
        entry_type: "Soul Work Reflection",
      });
      setSaved(true);
      toast.success("Held in your Journal.", {
        action: {
          label: "Open Journal",
          onClick: () => nav("/journal"),
        },
      });
    } catch {
      toast.error("Couldn't save just yet — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-12" data-testid={`soul-breath-page-${practice.slug}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <SoulBreadcrumb
          items={[
            { label: "Rituals", to: "/rituals" },
            { label: "Soul", to: "/soul" },
            { label: "Regulation & Presence", to: "/soul/regulation-presence" },
            { label: "Breath", to: "/soul/regulation-presence/breath" },
            { label: practice.title },
          ]}
        />
        <button
          type="button"
          onClick={() => nav("/soul/regulation-presence/breath")}
          data-testid="soul-breath-practice-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))] transition-colors"
        >
          <ArrowLeft size={12} /> Back to Breath
        </button>
      </div>

      <SoulHero
        emblem=""
        eyebrow={`Soul · Breath · ${practice.subtitle}`}
        title={practice.title}
        supporting={practice.tagline}
        image={practice.image}
        testid="soul-breath-practice-hero"
      />

      {/* THE PRACTICE — a one-sentence bridge between the hero and the
          interactive experience. Deliberately short so the member reads
          it in a breath and moves into the practice. */}
      <Panel label="The Practice" testid="breath-section-practice">
        <p className="font-display italic text-xl md:text-2xl leading-snug text-[hsl(var(--fg))]/85 max-w-3xl">
          {practice.thePractice}
        </p>
      </Panel>

      {/* BEGIN THE PRACTICE — the primary interactive focal point of the
          page. Luminous warm surface, soft amber halo behind the Begin
          button, generous negative space. The member can begin here
          without reading any further. */}
      <section
        id="begin"
        className="reveal reveal-1 relative overflow-hidden rounded-[24px] luminous-surface scroll-mt-24"
        data-testid="breath-player-section"
      >
        <div className="p-8 md:p-14">
          {/* Static N8ONE emblem floating in the top-right of the
              practice card. Marks the actual breath experience with the
              brand rather than the hero banner above the page. */}
          <div
            className="absolute top-5 right-6 md:top-7 md:right-9 pointer-events-none"
            data-testid="breath-practice-card-emblem-wrap"
          >
            <N8Emblem
              mode="simplified"
              size={36}
              ariaLabel="N8ONE emblem"
              testid="breath-practice-card-emblem"
            />
          </div>
          {mode === "read" && (
            <div data-testid="breath-player-setup" className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
                Begin the practice
              </div>
              <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic">
                When you&rsquo;re ready.
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
                Choose how long you&rsquo;d like to stay. You can pause or end anytime — nothing here has to
                be completed to count.
              </p>

              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  Duration
                </div>
                <div className="mt-3 inline-flex items-center gap-2 flex-wrap justify-center" role="group">
                  {practice.recommendedMinutes.map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMinutes(m)}
                      data-testid={`breath-duration-${m}`}
                      aria-pressed={minutes === m}
                      className={`px-4 py-1.5 rounded-full text-xs border hairline transition-all ${
                        minutes === m
                          ? "bg-[hsl(var(--copper))] text-[hsl(var(--paper))] border-transparent"
                          : "text-[hsl(var(--fg))]/75 hover:bg-[hsl(var(--sand))]/60"
                      }`}
                    >
                      {m} min
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  Optional intention
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap justify-center">
                  {BREATH_INTENTIONS.map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setIntention(intention === i ? null : i)}
                      data-testid={`breath-intention-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      aria-pressed={intention === i}
                      className={`px-3 py-1.5 rounded-full text-xs border hairline transition-all ${
                        intention === i
                          ? "bg-[hsl(var(--terracotta))]/25 text-[hsl(var(--fg))] border-[hsl(var(--terracotta))]/60"
                          : "text-[hsl(var(--fg))]/70 hover:bg-[hsl(var(--sand))]/60"
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))] italic">
                  A quiet layer, not a requirement.
                </p>
              </div>

              {/* Begin button in a small halo so it reads as the single
                  strongest actionable element on the page. */}
              <div className="mt-10 light-halo-sm inline-block">
                <Button
                  type="button"
                  onClick={() => setMode("practice")}
                  data-testid="breath-begin"
                  className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))] px-7"
                >
                  <Play size={14} className="mr-1.5" /> Begin
                </Button>
              </div>
            </div>
          )}

          {mode === "practice" && (
            <BreathPlayer
              phases={practice.phases}
              totalSeconds={minutes * 60}
              intention={intention}
              onComplete={handleComplete}
              onEnd={handleEnd}
            />
          )}

          {mode === "close" && (
            <div data-testid="breath-close" className="max-w-2xl mx-auto">
              <div className="text-center">
                <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
                  Reflection
                </div>
                <p className="font-display italic text-2xl md:text-3xl mt-3 leading-snug text-[hsl(var(--fg))]/85">
                  {practice.reflection}
                </p>
              </div>
              <Textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value)}
                rows={5}
                placeholder="A few words is enough…"
                className="mt-6 bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--copper))]"
                data-testid="breath-reflection-input"
              />
              <div className="mt-8 pt-8 border-t border-[hsl(var(--line))] text-center">
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
                  A gentle reminder
                </div>
                <p className="mt-3 font-display italic text-lg md:text-xl text-[hsl(var(--clay))] leading-snug">
                  {practice.reminder}
                </p>
              </div>
              <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
                <Button
                  type="button"
                  onClick={saveToJournal}
                  disabled={!reflection.trim() || saving || saved}
                  data-testid="breath-save-to-journal"
                  className="rounded-full bg-[hsl(var(--copper))] hover:bg-[hsl(var(--copper))]/90 text-[hsl(var(--paper))]"
                >
                  {saved ? (
                    <>
                      <Check size={14} className="mr-1.5" /> Held in Journal
                    </>
                  ) : (
                    <>
                      <Feather size={14} className="mr-1.5" />
                      {saving ? "Holding…" : "Save to Journal"}
                    </>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("read");
                    setReflection("");
                    setSaved(false);
                  }}
                  data-testid="breath-practice-again"
                  className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--copper))]"
                >
                  Practice again
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* GO DEEPER — a quiet typographic divider that signals the
          transition from EXPERIENCE to optional UNDERSTANDING. No large
          card, no icon, no decoration. */}
      <div
        className="pt-2 pb-1 text-center max-w-xl mx-auto"
        data-testid="breath-go-deeper-divider"
      >
        <div
          aria-hidden
          className="mx-auto h-px w-16 bg-gradient-to-r from-transparent via-[hsl(var(--copper))]/50 to-transparent"
        />
        <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--copper))]">
          Go Deeper
        </div>
        <p className="mt-2 text-[12.5px] italic text-[hsl(var(--muted-foreground))] leading-relaxed">
          Continue reading if you&rsquo;d like to understand the practice more fully.
        </p>
      </div>

      {/* Optional deeper understanding — Why / How / When / Notice. Grouped
          with tighter rhythm so related information feels connected while
          still breathing. */}
      <div className="space-y-10" data-testid="breath-deeper-sections">
        <Panel label="Why This Practice" testid="breath-section-why">
          <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85 max-w-3xl">
            {practice.why}
          </p>
        </Panel>

        <Panel label="How To Do It" testid="breath-section-how">
          <ol className="space-y-3 max-w-3xl">
            {practice.how.map((s, i) => (
              <li
                key={i}
                className="flex gap-4 text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85"
              >
                <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[hsl(var(--copper))]/12 text-[hsl(var(--copper))] text-xs flex items-center justify-center font-medium">
                  {i + 1}
                </span>
                <span>{s}</span>
              </li>
            ))}
          </ol>
        </Panel>

        <Panel label="When To Use It" testid="breath-section-when">
          <p className="text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/85 max-w-3xl">
            {practice.when}
          </p>
        </Panel>

        {/* WHAT TO NOTICE — slightly elevated. Very faint luminous warm
            surface + stronger copper vertical accent beside each line.
            Still invitations, not checklist items. */}
        <Panel label="What To Notice" testid="breath-section-notice" elevated>
          <ul className="space-y-4 max-w-3xl" data-testid="breath-notice-list">
            {practice.notice.map((line, i) => (
              <li
                key={i}
                className="relative pl-6 text-[15.5px] leading-relaxed text-[hsl(var(--fg))]/88"
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-1 bottom-1 w-[2px] rounded-full bg-gradient-to-b from-[hsl(var(--copper))] via-[hsl(var(--copper))]/70 to-[hsl(var(--copper-light))]/60"
                />
                {line}
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {/* Safety copy — always visible on every practice page. Kept
          calm and warm; readability over alarm. */}
      <section
        className="p-6 md:p-8 rounded-[20px] border hairline bg-[hsl(var(--sand))]/40 max-w-3xl scroll-mt-24"
        data-testid="soul-breath-practice-safety"
      >
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          A gentle note
        </div>
        <p className="mt-3 text-[14.5px] leading-relaxed text-[hsl(var(--fg))]/80">
          Never force the breath. If you feel dizzy, lightheaded, or uncomfortable, stop and return to
          your normal breathing. These practices do not treat or replace care for medical or
          mental-health conditions.
        </p>
      </section>
    </div>
  );
}

/**
 * Panel — the shared editorial heading + content block used by The
 * Practice and every "Go Deeper" section.
 *
 * The numeral prop was removed in the 2026-02 refinement because the
 * educational sections now live under a shared "Go Deeper" divider; a
 * running Roman-numeral count across those sections felt bureaucratic.
 *
 * `elevated` adds a very faint luminous warm wash + hairline — reserved
 * for "What To Notice" because that section is central to N8ONE's
 * witnessing orientation.
 */
function Panel({ label, children, testid, elevated = false }) {
  return (
    <section
      className={`reveal reveal-1 scroll-mt-24 ${
        elevated
          ? "rounded-[20px] p-6 md:p-8 bg-gradient-to-br from-[hsl(var(--sand))]/35 via-[hsl(var(--paper))] to-[hsl(var(--paper))] border hairline"
          : ""
      }`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))] inline-flex items-center gap-3">
        <span aria-hidden className="w-8 h-px bg-[hsl(var(--copper))]/40" />
        <span>{label}</span>
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}
